import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { TravelFolderIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useRef, useState } from 'react'

import { API } from '../../api/apis.config'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import TripFolderLock from '../../components/elements/trip-folder-lock'
import { TripFolderFilter } from '../../components/pages/trip-folders/trip-folder-filter'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { transportTypeMapping, tripTypeMapping } from '../../lib/types/enums/trip/trip-type.enum'
import { tripFolderTypeMapping } from '../../lib/types/enums/trip-folder/trip-folder-type.enum'
import { TripProperties } from '../../lib/types/models/trip/properties.trip'
import { TripFolder } from '../../lib/types/models/trip-folder/trip-folder'
import { type TripFolderFilters } from '../../lib/types/models/trip-folder/trip-folder-filtes'
import {
  transformTripFolderFilterToUrl,
  transformUrlToTripFolderFilter
} from '../../lib/utils/filters-mapping/trip-folder'
import { getTripFolderLabel } from '../../lib/utils/functions/trip-folder.functions'
import { getTripFolderTripSteps } from './utils'

const tripFolderFilters: TripFolderFilters = {
  year: new Date().getFullYear()
}

const TripFolderListPage = () => {
  const [isTableLoading, setIsTableLoading] = useState(false)

  const tableRef = useRef<DynamicTableRef<TripFolder>>(null)
  const { navigateWithEvent } = useNavigator()

  const { modalState, toggleModal, closeModel } = useModalManager<'filters', TripFolder[]>([
    'filters'
  ])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TripFolderFilters>({
      initialFilters: tripFolderFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTripFolderFilterToUrl,
      transformUrlToFilter: transformUrlToTripFolderFilter
    })

  const columns: Column<TripFolder>[] = [
    {
      accessorKey: 'date',
      header: 'Date',
      enableSorting: true,
      cell: ({ row }) => <Badge text={DateFormatter.getDayName(row.original.date)} />
    },
    {
      accessorKey: 'directionality',
      header: 'Type de trajet',
      enableSorting: true,
      cell: ({ row }) => {
        const { directionality } = tripFolderTypeMapping[row.original.type]
        const { label, variant } = transportTypeMapping[directionality]
        return <Badge text={label} variant={variant} />
      }
    },
    {
      accessorKey: 'type',
      header: 'Type de voyage',
      enableSorting: true,
      cell: ({ row }) => {
        const { tripType } = tripFolderTypeMapping[row.original.type]
        const tripTypeMap = tripTypeMapping[tripType]
        if (!tripType || !tripTypeMap) return '-'

        return <Badge text={tripTypeMap.label} variant={tripTypeMap.variant} />
      }
    },
    {
      accessorKey: 'trips.departure.label',
      header: 'Voyage',
      cell: ({ row: { original } }) => {
        if (original.trips.length === 0) return '-'
        return (
          <div className="flex flex-col gap-3.5 py-2">
            {original.trips.map((t) => (
              <Typography variant="secondary-body" key={t.id}>
                {TripProperties.getTripLabel(t)}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'departurePlace.label',
      header: 'Lieu de départ',
      cell: ({ row }) => (
        <div className="flex flex-col gap-3.5 py-2">
          {getTripFolderTripSteps(row.original).map((t) => (
            <div className="flex items-center gap-2" key={t.id}>
              <CountryFlag possibleCountry={t.departurePlace?.country} />
              <span>{t.departurePlace?.label ?? '-'}</span>
            </div>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'departureTime',
      header: 'Heure de départ',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 py-2">
          {getTripFolderTripSteps(row.original).map((t) => (
            <Badge
              key={t.id}
              text={valueRender(t.departureTime, (departureTime) =>
                DateFormatter.getFormattedTime(departureTime)
              )}
            />
          ))}
        </div>
      )
    },
    {
      accessorKey: 'arrivalPlace.label',
      header: "Lieu d'arrivée",
      cell: ({ row }) => (
        <div className="flex flex-col gap-3.5 py-2">
          {getTripFolderTripSteps(row.original).map((t) => (
            <div className="flex items-center gap-2" key={t.id}>
              <CountryFlag possibleCountry={t.arrivalPlace?.country} />
              <span>{t.arrivalPlace?.label ?? '-'}</span>
            </div>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'arrivalTime',
      header: "Heure d'arrivée",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 py-2">
          {getTripFolderTripSteps(row.original).map((t) => (
            <Badge
              key={t.id}
              text={valueRender(t.arrivalTime, (arrivalTime) =>
                DateFormatter.getFormattedTime(arrivalTime)
              )}
            />
          ))}
        </div>
      )
    },
    {
      accessorKey: 'locked',
      header: 'Statut',
      enableSorting: true,
      cell: ({ row }) => {
        const isLocked = row.original.locked
        return (
          <TripFolderLock
            id={row.original.id}
            label={getTripFolderLabel(row.original)}
            locked={isLocked}
            onSuccess={() => tableRef.current?.refetch()}
            isTableLoading={isTableLoading}
          />
        )
      }
    }
  ]

  return (
    <div>
      <UserNavBar title="Dossier Voyage" icon={TravelFolderIcon} />
      <div className="h-4" />
      <DynamicDataTable<TripFolder, TripFolderFilters>
        ref={tableRef}
        title="Tous les dossiers"
        columns={columns}
        deleteMessage={{ name: 'dossier', attributeName: 'id' }}
        selectable
        allowSearchParam
        isSearchable={false}
        onLoadingChange={setIsTableLoading}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        endpoint={API.tripFolder.getAll.url}
        onRowClick={(row, event) =>
          navigateWithEvent(
            Paths.TRIP_FOLDER_DETAILS_PAGE.replace(':id', row.id.toString()).replace('/*', ''),
            event
          )
        }
      />

      <FilterDrawer<TripFolderFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <TripFolderFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default TripFolderListPage
